import React, { useState, useEffect, useRef } from "react";
import {
  MapPin,
  ChevronLeft,
  ChevronRight,
  Play,
  Pause,
  Volume2,
  VolumeX,
} from "lucide-react";

// Video reels - playable .mov / .mp4 content
const videoReels = [
  {
    type: "video" as const,
    videoSrc:
      "https://assets.cdn.filesafe.space/4lOsVXfRzB21f2Hlg8pa/media/6a863b31949d6f49c3e00e44.mov",
    posterImage: "",
    title: "Fence Replacement",
    location: "Raleigh, NC",
    date: "August 2026",
  },
  {
    type: "video" as const,
    videoSrc:
      "https://assets.cdn.filesafe.space/4lOsVXfRzB21f2Hlg8pa/media/6a863b31ad59e6cfedabcbb7.mov",
    posterImage: "",
    title: "Fence Replacement",
    location: "Raleigh, NC",
    date: "August 2026",
  },
];

export function ReelShowcase() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);

  const reels = [...videoReels];

  // Video playback control
  useEffect(() => {
    if (reels[currentIndex]?.type === "video" && videoRef.current) {
      if (isPlaying) {
        videoRef.current.play().catch(() => {});
      } else {
        videoRef.current.pause();
      }
    }
  }, [currentIndex, isPlaying]);

  // Reset state when switching reels
  useEffect(() => {
    setIsPlaying(true);
  }, [currentIndex]);

  const currentReel = reels[currentIndex];

  const goNext = () => setCurrentIndex((prev) => (prev + 1) % reels.length);
  const goPrev = () =>
    setCurrentIndex((prev) => (prev - 1 + reels.length) % reels.length);

  const togglePlayPause = () => {
    if (currentReel.type === "video" && videoRef.current) {
      if (videoRef.current.paused) {
        videoRef.current.play().catch(() => {});
        setIsPlaying(true);
      } else {
        videoRef.current.pause();
        setIsPlaying(false);
      }
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(videoRef.current.muted);
    }
  };

  const isVideo = currentReel?.type === "video";

  return (
    <section className="bg-black py-12 lg:py-16 border-t border-white/5">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-8">
          <span className="text-sm font-bold tracking-widest text-primary uppercase mb-3 block">
            VIDEO REELS
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-2">
            Watch Our Transformations
          </h2>
          <p className="text-gray-400 max-w-xl mx-auto text-sm md:text-base">
            Swipe through before & after reels of our recent work.
          </p>
        </div>

        <div className="flex flex-col lg:flex-row items-center justify-center gap-6 lg:gap-10">
          {/* Reel Container - Vertical 9:16 */}
          <div className="relative w-full max-w-[320px] mx-auto">
            {/* Phone frame */}
            <div className="relative aspect-[9/16] rounded-3xl overflow-hidden bg-gray-900 shadow-2xl border-2 border-white/10 select-none">
              {/* Video Reel */}
              {isVideo && (
                <video
                  ref={videoRef}
                  src={currentReel.videoSrc}
                  className="absolute inset-0 w-full h-full object-cover pointer-events-none"
                  autoPlay
                  muted={isMuted}
                  loop
                  playsInline
                  preload="metadata"
                  controls={false}
                  onClick={(e) => e.preventDefault()}
                />
              )}

              {/* Gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/80 pointer-events-none" />

              {/* Video badge */}
              {isVideo && (
                <div className="absolute top-4 left-4 z-20">
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-bold text-white transition-colors duration-300 ${currentIndex === 0 ? "bg-red-500" : "bg-green-500"}`}
                  >
                    {currentIndex === 0 ? "BEFORE" : "AFTER"}
                  </span>
                </div>
              )}

              {/* Play/Pause & Mute controls (video reels only) */}
              {isVideo && (
                <>
                  <button
                    onClick={togglePlayPause}
                    className="absolute top-4 right-4 z-20 w-9 h-9 rounded-full bg-black/50 backdrop-blur-md flex items-center justify-center text-white hover:bg-black/70 transition-colors"
                    aria-label={isPlaying ? "Pause video" : "Play video"}
                  >
                    {isPlaying ? (
                      <Pause className="w-4 h-4" />
                    ) : (
                      <Play className="w-4 h-4" />
                    )}
                  </button>
                  <button
                    onClick={toggleMute}
                    className="absolute top-14 right-4 z-20 w-9 h-9 rounded-full bg-black/50 backdrop-blur-md flex items-center justify-center text-white hover:bg-black/70 transition-colors"
                    aria-label={isMuted ? "Unmute video" : "Mute video"}
                  >
                    {isMuted ? (
                      <VolumeX className="w-4 h-4" />
                    ) : (
                      <Volume2 className="w-4 h-4" />
                    )}
                  </button>
                </>
              )}

              {/* Bottom caption */}
              <div className="absolute bottom-0 left-0 right-0 p-4 z-10">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 bg-sidebar-background">
                    <img
                      src="https://vibe.filesafe.space/1781836485235618884/attachments/488cd5a2-77dc-4f41-a684-dd6ede168f71.png"
                      alt="Long Vista"
                      className="w-6 h-6 object-contain rounded-full bg-sidebar-primary-foreground"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  <span className="text-white font-bold text-sm">
                    @longvistaremodeling
                  </span>
                </div>
                <p className="text-white text-sm font-medium mb-1">
                  {currentReel.title}
                </p>
                <div className="flex items-center gap-2 text-gray-300 text-xs">
                  <MapPin className="w-3 h-3" />
                  <span>{currentReel.location}</span>
                  <span>•</span>
                  <span>{currentReel.date}</span>
                </div>
              </div>
            </div>

            {/* Navigation arrows */}
            <button
              onClick={goPrev}
              className="absolute -left-14 top-1/2 -translate-y-1/2 hidden lg:flex w-10 h-10 rounded-full bg-white/10 border border-white/20 items-center justify-center text-white hover:bg-white/20 transition-colors"
              aria-label="Previous reel"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={goNext}
              className="absolute -right-14 top-1/2 -translate-y-1/2 hidden lg:flex w-10 h-10 rounded-full bg-white/10 border border-white/20 items-center justify-center text-white hover:bg-white/20 transition-colors"
              aria-label="Next reel"
            >
              <ChevronRight className="w-5 h-5" />
            </button>

            {/* Mobile navigation */}
            <div className="lg:hidden flex items-center justify-center gap-4 mt-4">
              <button
                onClick={goPrev}
                className="w-10 h-10 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
                aria-label="Previous reel"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <span className="text-gray-500 text-xs">
                Reel {currentIndex + 1} of {reels.length}
              </span>
              <button
                onClick={goNext}
                className="w-10 h-10 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
                aria-label="Next reel"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Info Panel - Desktop */}
          <div className="hidden lg:block max-w-xs">
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
              <h3 className="text-white font-bold text-lg mb-2">
                {currentReel.title}
              </h3>
              <div className="flex items-center gap-2 text-gray-400 text-sm mb-4">
                <MapPin className="w-4 h-4" />
                <span>{currentReel.location}</span>
                <span>•</span>
                <span>{currentReel.date}</span>
              </div>
              <p className="text-gray-300 text-sm leading-relaxed mb-4">
                Watch this project come to life. Tap the video to play or pause,
                and use the mute button to toggle sound.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={goPrev}
                  className="flex-1 bg-white/10 hover:bg-white/20 text-white text-sm font-semibold py-2.5 rounded-lg transition-colors"
                >
                  Previous
                </button>
                <button
                  onClick={goNext}
                  className="flex-1 bg-primary hover:bg-primary/90 text-white text-sm font-semibold py-2.5 rounded-lg transition-colors"
                >
                  Next Reel
                </button>
              </div>
              <div className="mt-3 text-center text-gray-500 text-xs">
                Reel {currentIndex + 1} of {reels.length}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
